import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Bug } from "lucide-react";
import { http } from "../api/http";
import { studentMessagingError } from "../api/studentMessaging";

/** Adresse de contact publique de Permio : le repli quand un signalement ne part pas. */
const SUPPORT_EMAIL = "contact@permio.fr";
const MIN_LENGTH = 5;
const MAX_LENGTH = 4000;

/**
 * Signalement d'un problème au service technique Permio, depuis les messages.
 * Ce n'est pas une conversation : le texte part par e-mail, et la réponse arrive par e-mail.
 */
export default function BugReport({ onClose }: { onClose: () => void }) {
  const [message, setMessage] = useState("");
  const text = message.trim();
  const send = useMutation({
    mutationFn: async () =>
      (
        await http.post<{ sent: boolean; replyTo: string | null }>(
          "/v1/support/reports",
          // Le compte et le navigateur sont ajoutés par le serveur ; seule la page part d'ici.
          { message: text, page: window.location.pathname },
        )
      ).data,
  });
  const back = (
    <button
      type="button"
      className="messaging-back"
      aria-label="Retour"
      title="Retour aux conversations"
      onClick={onClose}
    >
      <ArrowLeft size={18} aria-hidden="true" />
    </button>
  );
  if (send.isSuccess)
    return (
      <div className="messaging-report">
        <header>
          {back}
          <h2>Signalement envoyé</h2>
        </header>
        <p role="status">
          Merci. Votre message est parti au service technique Permio.{" "}
          {send.data.replyTo
            ? `Nous vous répondrons par e-mail à ${send.data.replyTo}.`
            : "Nous vous répondrons par e-mail."}
        </p>
        <button
          type="button"
          className="messaging-button messaging-button--secondary"
          onClick={onClose}
        >
          Retour aux messages
        </button>
      </div>
    );
  return (
    <div className="messaging-report">
      <header>
        {back}
        <h2>Signaler un problème</h2>
        <span className="messaging-report-mark">
          <Bug size={12} aria-hidden="true" />
          Service technique Permio
        </span>
      </header>
      <form
        className="messaging-form"
        aria-label="Signaler un problème"
        onSubmit={(event) => {
          event.preventDefault();
          if (text.length >= MIN_LENGTH && !send.isPending) send.mutate();
        }}
      >
        <label>
          Que s’est-il passé ?
          <textarea
            className="messaging-field"
            rows={5}
            required
            minLength={MIN_LENGTH}
            maxLength={MAX_LENGTH}
            placeholder="Ce que vous faisiez, ce qui s’est passé, ce que vous attendiez."
            value={message}
            readOnly={send.isPending}
            onChange={(event) => {
              if (send.isError) send.reset();
              setMessage(event.target.value);
            }}
          />
        </label>
        <p className="messaging-hint">
          Votre message part par e-mail au service technique Permio, avec votre
          nom, votre adresse e-mail, votre école, la page ouverte et votre
          navigateur. Rien d’autre n’est joint : ne collez pas de données
          d’élève dont nous n’aurions pas besoin.
        </p>
        {send.isError && (
          <p role="alert" className="messaging-alert">
            {studentMessagingError(
              send.error,
              `Le signalement n’a pas pu être envoyé. Écrivez-nous à ${SUPPORT_EMAIL}.`,
            )}{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>Ouvrir un e-mail</a>
          </p>
        )}
        <div className="messaging-form-actions">
          <button
            type="button"
            className="messaging-button messaging-button--secondary"
            disabled={send.isPending}
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="messaging-button"
            disabled={text.length < MIN_LENGTH || send.isPending}
          >
            {send.isPending ? "Envoi…" : "Envoyer le signalement"}
          </button>
        </div>
      </form>
    </div>
  );
}
